import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  AutoAwesome as DraftIcon,
  ThumbUp as ApprovedIcon,
  Send as PostedIcon,
  DoneAll as CompletedIcon,
  Pause as PausedIcon,
  PlayArrow as ActiveIcon,
  Block as VoidIcon,
} from '@mui/icons-material';
import { ledgerAPI } from '../../../services/api';
import { LedgerPicker, useLedgerOptions } from '../LedgerPickers';

// ---------------------------------------------------------------------------
// Shared Income / Revenue Recognition UI kit - mirrors receivables/ArShared.js
// and payables/ApShared.js so this module reads as one product with the
// rest of the ledger.
// ---------------------------------------------------------------------------

export const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

export const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

export const unwrapList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

export const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_CFG = {
  DRAFT: { label: 'Draft', color: 'default', Icon: DraftIcon },
  APPROVED: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  POSTED: { label: 'Posted', color: 'info', Icon: PostedIcon },
  COMPLETED: { label: 'Completed', color: 'success', Icon: CompletedIcon },
  ACTIVE: { label: 'Active', color: 'success', Icon: ActiveIcon },
  PAUSED: { label: 'Paused', color: 'default', Icon: PausedIcon },
  VOID: { label: 'Void', color: 'error', Icon: VoidIcon },
};

export const IncomeStatusChip = ({ status }) => {
  const st = String(status || 'DRAFT').toUpperCase();
  const cfg = STATUS_CFG[st] || { label: status || 'Draft', color: 'default', Icon: DraftIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

export const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export const StatStrip = ({ tiles = [] }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);

// Schedule picker for filtering recognitions by their parent revenue
// schedule - same useLedgerOptions cache-and-fetch idiom as every other
// picker in LedgerPickers.js, just scoped to this module's own endpoint.
export const SchedulePicker = (props) => {
  const { options, loading } = useLedgerOptions('income-revenue-schedules', () =>
    ledgerAPI.getRevenueSchedules({ limit: 100 }).then((res) => {
      const payload = res?.data?.data ?? res?.data ?? {};
      const list = Array.isArray(payload) ? payload : payload.items ?? [];
      return list.map((s) => ({
        value: s.schedule_id ?? s.id,
        label: s.schedule_number || s.description || `Schedule ${s.schedule_id ?? s.id}`,
      }));
    })
  );
  return <LedgerPicker label="Schedule" {...props} options={options} loading={loading} />;
};
